/**
 * i18n types - locale and message tree.
 * Locale stored in localStorage under key LOCALE_STORAGE_KEY.
 */

export const LOCALE_STORAGE_KEY = "intake_locale";
/** @deprecated Superseded by LOCALE_STORAGE_KEY; read-only for migrating existing sessions. */
export const LEGACY_LOCALE_STORAGE_KEY = "intake_locale";

export type Locale = "en" | "vi";

export const LOCALES: Locale[] = ["en", "vi"];

export const DEFAULT_LOCALE: Locale = "en";

/** Nested message tree: values are strings or nested objects. */
export interface MessageTree {
  [key: string]: string | MessageTree;
}

/** Domain terms that stay English inside Vietnamese UI copy when they name product modules or protocols. */
export const LOCALE_KEEP_ENGLISH_TERMS = [
  "Intake Hub",
  "Platform 9S",
  "Nice Solutions",
  "Keycloak",
  "IAM",
  "OAuth",
  "Draft",
  "Triage",
  "Thread digest",
  "Metric & Quote",
  "LLM",
  "RAG",
  "source system",
  "IMAP",
  "SMTP",
  "SharePoint",
  "Gmail",
  "Microsoft Graph",
] as const;

/** Keys where vi intentionally matches en (brand, protocol labels, API field names, task slugs). */
export const ALLOWED_IDENTICAL_VI_KEYS = [
  "admin.hub.cards.emailIntake",
  "ai.models.colFamily",
  "ai.models.colProvider",
  "ai.models.colQuant",
  "app.name",
  "auth.platformTitle",
  "auth.showcase.brandSubtitle",
  "auth.showcase.modules.aiRag.chip1",
  "auth.showcase.modules.aiRag.chip2",
  "auth.showcase.modules.aiRag.chip3",
  "auth.showcase.modules.aiRag.title",
  "auth.showcase.modules.content.chip1",
  "auth.showcase.modules.content.chip2",
  "auth.showcase.modules.content.chip3",
  "auth.showcase.modules.content.title",
  "auth.showcase.modules.finance.chip1",
  "auth.showcase.modules.finance.chip2",
  "auth.showcase.modules.finance.chip3",
  "auth.showcase.modules.finance.title",
  "auth.showcase.modules.governance.chip1",
  "auth.showcase.modules.governance.chip2",
  "auth.showcase.modules.governance.chip3",
  "auth.showcase.modules.governance.title",
  "auth.showcase.modules.intake.chip1",
  "auth.showcase.modules.intake.chip2",
  "auth.showcase.modules.intake.chip3",
  "auth.showcase.modules.intake.title",
  "auth.showcase.modules.search.chip1",
  "auth.showcase.modules.search.chip2",
  "auth.showcase.modules.search.chip3",
  "auth.showcase.modules.search.title",
  "auth.showcase.modules.strategy.chip1",
  "auth.showcase.modules.strategy.chip2",
  "auth.showcase.modules.strategy.chip3",
  "auth.showcase.modules.strategy.title",
  "auth.showcase.modules.work.chip1",
  "auth.showcase.modules.work.chip2",
  "auth.showcase.modules.work.chip3",
  "auth.showcase.modules.work.title",
  "common.accessDeniedCode",
  "common.empty",
  "content.capture.stepUpload",
  "content.capture.uploadSection",
  "content.common.amend",
  "content.common.approve",
  "content.common.approveCount",
  "content.common.confidence",
  "content.common.docType",
  "content.common.itemSuffix",
  "content.common.itemsSuffix",
  "content.common.reject",
  "content.common.rejectCount",
  "content.library.approvedVersion",
  "content.library.contentRef",
  "content.library.drive",
  "content.library.mime",
  "content.library.sortStorage",
  "content.review.amendedFolder",
  "content.review.chipType",
  "content.review.colConfidence",
  "content.review.colDocument",
  "content.review.colType",
  "content.review.draftFolder",
  "content.review.draftType",
  "content.review.draftVsAmendment",
  "content.review.pageAria",
  "content.review.timelineApproved",
  "content.review.timelineCaptured",
  "content.review.timelineClassified",
  "content.review.timelineDecision",
  "content.review.timelineMoved",
  "content.review.timelinePublished",
  "content.review.timelineRejected",
  "content.review.timelineReviewed",
  "content.storage.active",
  "content.storage.inboundSync",
  "content.storage.resetCache",
  "customerWorkspace.channel.api",
  "customerWorkspace.channel.chatwoot",
  "customerWorkspace.channel.chatwootWeb",
  "customerWorkspace.channel.discord",
  "customerWorkspace.channel.email",
  "customerWorkspace.channel.sms",
  "customerWorkspace.channel.zalo",
  "customerWorkspace.headlineSep",
  "customerWorkspace.identityType.email",
  "customerWorkspace.participant.bot",
  "customerWorkspace.work.sla",
  "dashboard.endpoints",
  "dashboard.metricUptime",
  "dashboard.port",
  "dashboard.title",
  "dashboard.uptime",
  "dashboard.uptimeValue",
  "finance.invoices.colVat",
  "hubSync.progressEta",
  "hubSync.progressNoEstimate",
  "intakeDrafts.colCustomer",
  "intakeDrafts.colCustomerMask",
  "intakeDrafts.mailDirectionInbound",
  "intakeDrafts.mailDirectionInternal",
  "intakeDrafts.mailDirectionOutbound",
  "intakeDrafts.threadBodyToggleDigest",
  "intakeDrafts.threadGroupBadge",
  "intakeDrafts.threadMessageMetaLabel",
  "intakeDrafts.threadMessageProject",
  "intakeDrafts.threadMessageRecordId",
  "intakeDrafts.triageCustomer",
  "intakeLedger.colRecordId",
  "intakeLedger.colTaskId",
  "intakeLedger.detailActorRaw",
  "intakeLedger.detailEntryId",
  "intakeLedger.detailProvider",
  "intakeLedger.filterProjectId",
  "intakeLedger.filterRecordId",
  "intakeLedger.filterTaskId",
  "intakeRecords.attachmentInlineBadge",
  "intakeRecords.colId",
  "intakeRecords.colThreadId",
  "intakeRecords.pipelineTriageStaleLabel",
  "intakeRecords.threadDigestStaleBadge",
  "intakeRecords.triageClassificationCustomerAccount",
  "intakeRecords.triageClassificationEmptyPlaceholder",
  "intakeRecords.triageHistoryRunLabel",
  "intakeRecords.triageProvenanceContract",
  "intakeRecords.triageProvenanceFrozenPack",
  "intakeRecords.triageProvenanceModel",
  "intakeRecords.triageProvenancePipelineRun",
  "intakeRecords.triageProvenanceTemplate",
  "intakeRecords.triageRawJsonTitle",
  "intakeRecords.triageRuntimeFrozenPackLabel",
  "intakeRecords.triageRuntimeTemplateIdLabel",
  "intakeRecords.triageStatusStale",
  "intakeRecords.triageWorkspaceTitle",
  "intakeRecords.workflowDraftTitle",
  "monitoring.activeServices",
  "monitoring.avgResponseTime",
  "monitoring.checked",
  "monitoring.cpuUsage",
  "monitoring.diskUsage",
  "monitoring.endpoints",
  "monitoring.errorRate",
  "monitoring.healthyServices",
  "monitoring.latency",
  "monitoring.memoryUsage",
  "monitoring.monitoredServices",
  "monitoring.networkThroughput",
  "monitoring.port",
  "monitoring.probeError",
  "monitoring.serviceStatus",
  "monitoring.uptime",
  "nav.customView",
  "nav.draft",
  "nav.iamAuthz",
  "nav.iamServiceItems",
  "nav.requestInfo",
  "nav.requestInfoTemplates",
  "nav.threadDigest",
  "nav.triage",
  "navSection.customer",
  "platform.search.contextIntents.compliance",
  "platform.search.contextIntents.planning",
  "platform.search.contextIntents.risk_review",
  "platform.search.contextIntents.timeline_update",
  "platform.search.sources.content",
  "platform.search.sources.contracts",
  "platform.search.sources.finance",
  "platform.search.sources.projects",
  "platform.search.sources.records",
  "platform.search.sources.tasks",
  "rag.documents.detailEyebrow",
  "rag.documents.title",
  "rag.ingest.batch",
  "rag.ingest.colUrl",
  "rag.ingest.crawlUrlPlaceholder",
  "rag.ingest.healthConnected",
  "rag.ingest.healthDisconnected",
  "rag.ingest.off",
  "rag.ingest.ok",
  "rag.projects.colDocs",
  "rag.projects.documents",
  "route.adminOperational.optionFalse",
  "route.adminOperational.optionTrue",
  "route.governance.integrations.diagnostics.customFieldOwnerExternalSystem",
  "route.governance.integrations.diagnostics.customFieldRow",
  "route.governance.integrations.diagnostics.customFieldTargetSnapshot",
  "route.governance.integrations.diagnostics.linePollCursor",
  "route.governance.integrations.diagnostics.linePollTaskId",
  "route.governance.integrations.diagnostics.ruleContractDetail.activeFlagSelectFalse",
  "route.governance.integrations.diagnostics.ruleContractDetail.activeFlagSelectTrue",
  "route.governance.integrations.diagnostics.ruleContractDetail.placeholderActionOutputJson",
  "route.governance.integrations.diagnostics.ruleContractDetail.placeholderConditionJson",
  "route.governance.integrations.diagnostics.ruleContractDetail.placeholderEffectiveFrom",
  "route.governance.integrations.diagnostics.ruleContractDetail.placeholderEffectiveTo",
  "route.governance.integrations.diagnostics.ruleContractDetail.placeholderExceptionJson",
  "route.governance.integrations.diagnostics.ruleContractDetail.placeholderFallbackJson",
  "route.governance.integrations.diagnostics.ruleContractDetail.placeholderRuleCategory",
  "route.governance.integrations.diagnostics.ruleContractDetail.placeholderRuleDomain",
  "route.governance.integrations.diagnostics.ruleContractDetail.placeholderRuleId",
  "route.governance.integrations.diagnostics.ruleContractDetail.placeholderUpdatedBy",
  "route.governance.integrations.diagnostics.setupCheck.requestId",
  "route.governance.integrations.diagnostics.syncHistory.columns.payload",
  "route.governance.integrations.diagnostics.syncHistory.columns.response",
  "route.governance.integrations.diagnostics.syncHistory.columns.retry",
  "route.governance.integrations.diagnostics.syncHistory.columns.status",
  "route.governance.integrations.diagnostics.syncHistory.rowIdKey",
  "route.governance.integrations.diagnostics.syncHistory.runtimeIdKey",
  "route.governance.integrations.fields.mail_oauth_access_token",
  "route.governance.integrations.fields.mail_oauth_client_id",
  "route.governance.integrations.fields.mail_oauth_client_secret",
  "route.governance.integrations.fields.mail_oauth_refresh_token",
  "route.governance.integrations.fields.mail_oauth_tenant_id",
  "route.governance.integrations.fields.mail_sync_cursor",
  "route.governance.integrations.fields.sharepoint_client_id",
  "route.governance.integrations.fields.sharepoint_drive_id",
  "route.governance.integrations.fields.sharepoint_probe_path",
  "route.governance.integrations.fields.sharepoint_site_id",
  "route.governance.integrations.fields.sharepoint_tenant_id",
  "route.governance.integrations.groupExternalSystem",
  "route.governance.integrations.groupSharepoint",
  "route.governance.integrations.mailProviders.gmailApi",
  "route.governance.integrations.mailProviders.imapSmtp",
  "route.governance.integrations.mailProviders.microsoftGraph",
  "route.root.title",
  "shared.document.title",
  "templateManager.entityRequestInfo",
  "templateManager.scopeMineShort",
  "templateManager.scopeOrgShort",
  "userWorkspaceSettings.mailConnectionModeGmail",
  "userWorkspaceSettings.mailConnectionModeImap",
  "userWorkspaceSettings.mailConnectionModeMicrosoft",
  "userWorkspaceSettings.mailReadinessOAuth",
  "work.tasks.downloadFile",
  "work.tasks.fileLabel",
  "work.tasks.idPrefix",
  "workflowAiPolicyPage.catalog.contextWindow",
  "workflowAiPolicyPage.catalog.maxOutput",
  "workflowAiPolicyPage.catalog.output",
  "workflowAiPolicyPage.catalog.region",
  "workflowAiPolicyPage.catalog.streaming",
  "workflowAiPolicyPage.catalog.tools",
  "workflowAiPolicyPage.catalog.version",
  "workflowAiPolicyPage.preview.runtime",
  "workflowAiPolicyPage.previewKeys.llm_budget_headroom_tokens",
  "workflowAiPolicyPage.runtime.aiService",
  "workflowAiPolicyPage.runtime.project",
  "workflowAiPolicyPage.runtime.region",
  "workflowAiPolicyPage.runtime.transport",
  "workflowAiPolicyPage.tasks.analyze",
  "workflowAiPolicyPage.tasks.clean",
  "workflowAiPolicyPage.tasks.draft",
  "workflowAiPolicyPage.tasks.sentiment",
  "workflowAiPolicyPage.tasks.triage",
  "workflowAiPolicyPage.units.temperature",
  // Brands, protocols, acronyms, units, placeholders, and machine-facing identifiers.
  "nav.intake",
  "navSection.intake",
  "navSection.aiPlatform",
  "navSection.iamAccess",
  "rag.preRagReview.extractionProvenanceEasyocr",
  "rag.preRagReview.extractionProvenanceEasyocrRepair",
  "rag.preRagReview.sizeBytes",
  "rag.preRagReview.ageHours",
  "rag.preRagReview.approvalProgress",
  "rag.documents.projectionLabel",
  "rag.documents.ageHours",
  "route.knowledgeGovernance.declarationPrDraftStatus",
  "route.knowledgeGovernance.orgUnitId",
  "route.knowledgeGovernance.opsOutboxDlq",
  "consultation.ops.resolveEmail",
  "customerWorkspace.channel.facebook",
  "customerWorkspace.channel.messenger",
  "customerWorkspace.overview.actionWithIdentifier",
  "customerWorkspace.overview.commitmentSla",
  "customerWorkspace.governance.profileFields.website",
  "customerWorkspace.governance.profileFields.email",
  "customerWorkspace.legalFields.email",
  "userWorkspaceSettings.profile.email",
  "userWorkspaceSettings.aiDefaults.draftLabel",
  "userWorkspaceSettings.aiDefaults.triageLabel",
  "intakeChannel.email",
  "intakeChannel.discord",
  "intakeChannel.zalo",
  "intakeChannel.facebook",
  "intakeDrafts.contextSegmentTriage",
  "intakeDrafts.fieldCc",
  "auth.logoutConfirmUnknownRole",
  "dashboard.brand",
  "dashboard.pulse.knowledge_prerag",
  "dashboard.heroSummaryPrerag",
  "dashboard.refreshing",
  "content.review.extractionProvenanceEasyocr",
  "content.review.extractionProvenanceEasyocrRepair",
  "content.library.detailUnknown",
  "content.storage.provider.platform_s3",
  "content.storage.provider.google_drive",
  "commercial.brand",
  "commercial.pricing.plans.professional",
  "commercial.pricing.plans.business",
  "commercial.pricing.plans.enterprise",
  "commercial.pricing.compareGroups.AI",
] as const;
